import { lineTotal, safeNumber } from "@/lib/quoteNumbers";
// Mobile-only Quote Builder experience (<768px). Renders alongside the
// unchanged desktop QuoteWorkspace — same `q` state, same patch()/updateItem()
// handlers, same APIs. No new quote data model, no new calculations beyond a
// purely-visual cost/margin readout that already exists as raw fields on the
// desktop item editor.
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ChevronRight, ChevronDown, Building2, FileText, Layers, Library, Plus,
  MoreVertical, Search, Minus, CircleDollarSign, Check, Eye, Send, X, Percent,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useKiriClearance } from "@/lib/useKiriClearance";
import {
  LinkedJobSection, AttachmentsSection, NotesSection, TermsSection, SettingsPanel,
  QuickActionsPanel, ActivitySection,
} from "@/components/QuoteWorkspaceParts";

const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(safeNumber(n));
const lineCost = (it) => {
  if (it.labour_cost == null && it.material_cost == null && it.plant_cost == null) return null;
  return safeNumber(it.labour_cost) + safeNumber(it.material_cost) + safeNumber(it.plant_cost);
};
const RECENT_RATES_KEY = "fl_recent_rates";

function useMeasuredHeight() {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setH(entries[0].contentRect.height));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, h];
}

// ==================== generic full-screen mobile sheet ====================
function MobileSheet({ open, onClose, eyebrow = "QUOTE", title, children, footer, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[75] bg-white dark:bg-[#0B0F14] flex flex-col md:hidden" data-testid={testId}>
      <div className="flex items-center gap-3 px-4 h-14 border-b border-zinc-200 dark:border-white/10 shrink-0">
        <button onClick={onClose} aria-label="Back" className="text-zinc-500 hover:text-[#FF5F15] p-1 -ml-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">{eyebrow}</div>
          <div className="font-display font-black uppercase tracking-tight text-sm truncate">{title}</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
      {footer && <div className="shrink-0 border-t border-zinc-200 dark:border-white/10 p-3">{footer}</div>}
    </div>
  );
}

// ==================== collapsed summary card ====================
function CollapsedCard({ icon: Icon, label, count, onAction, actionLabel = "CHANGE", children, testId }) {
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]" data-testid={testId}>
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-zinc-100 dark:border-white/5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />} {label} {count != null && <span className="text-zinc-400">{count}</span>}
        </div>
        {onAction && (
          <button onClick={onAction} className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] flex items-center gap-0.5">
            {actionLabel} <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="px-3.5 py-3">{children}</div>
    </section>
  );
}

// ==================== 1. CUSTOMER ====================
export function MobileCustomerCard({ q, patch, clients, setClients, editable }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "", address: "" });
  const selected = clients.find((c) => c.id === q.client_id) ||
    (q.client_name ? { name: q.client_name, email: q.client_email, address: q.client_address } : null);
  const filtered = clients.filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase()));

  const pick = (c) => { patch({ client_id: c.id, client_name: c.name, client_email: c.email || "", client_address: c.address || "" }); setOpen(false); };
  const addClient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/clients", form);
      setClients([data, ...clients]);
      pick(data);
      setAdding(false);
      setForm({ name: "", contact_person: "", email: "", phone: "", address: "" });
    } catch { toast.error("Could not add customer"); }
  };

  return (
    <>
      <CollapsedCard icon={Building2} label="Customer" testId="mobile-quote-customer"
        onAction={editable ? () => setOpen(true) : null} actionLabel={selected ? "CHANGE" : "SELECT"}>
        {selected ? (
          <div data-testid="mobile-customer-summary">
            <div className="font-bold text-sm">{selected.name}</div>
            {selected.contact_person && <div className="text-xs text-zinc-500 mt-0.5">{selected.contact_person}</div>}
            {!selected.contact_person && selected.email && <div className="text-xs text-zinc-500 mt-0.5">{selected.email}</div>}
          </div>
        ) : (
          <div className="text-center py-3" data-testid="mobile-no-customer">
            <div className="text-xs text-zinc-400 mb-2">No customer selected</div>
            {editable && (
              <Button size="sm" onClick={() => setOpen(true)} className="rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider h-9" data-testid="mobile-select-customer-btn">
                Select Customer
              </Button>
            )}
          </div>
        )}
      </CollapsedCard>

      <MobileSheet open={open} onClose={() => setOpen(false)} title="Select customer" testId="mobile-customer-sheet">
        {adding ? (
          <form onSubmit={addClient} className="space-y-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">New customer</div>
            <Input required autoFocus placeholder="Company / customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-lg" data-testid="mobile-new-customer-name" />
            <Input placeholder="Contact person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="h-11 rounded-lg" />
            <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-lg" data-testid="mobile-new-customer-email" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-lg" />
            <Input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 rounded-lg" />
            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setAdding(false)} className="flex-1 h-11 rounded-lg">Cancel</Button>
              <Button type="submit" className="flex-1 h-11 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="mobile-new-customer-save">Save</Button>
            </div>
          </form>
        ) : (
          <>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input autoFocus placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 rounded-lg pl-9" data-testid="mobile-customer-search" />
            </div>
            <div className="space-y-1">
              {filtered.map((c) => (
                <button key={c.id} onClick={() => pick(c)} className="w-full text-left px-3 py-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-white/5 border border-transparent hover:border-zinc-200 dark:hover:border-white/10" data-testid={`mobile-customer-option-${c.id}`}>
                  <div className="font-bold text-sm">{c.name}</div>
                  {c.email && <div className="text-xs text-zinc-500">{c.email}</div>}
                </button>
              ))}
              {filtered.length === 0 && <div className="py-6 text-center text-xs text-zinc-400">No matches</div>}
            </div>
            <button onClick={() => setAdding(true)} className="w-full mt-3 py-3 border-t border-zinc-100 dark:border-white/5 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15]" data-testid="mobile-add-new-customer">
              + Add New Customer
            </button>
          </>
        )}
      </MobileSheet>
    </>
  );
}

// ==================== 2. QUOTE DETAILS ====================
export function MobileDetailsCard({ q, patch, validDays }) {
  const [open, setOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const adv = q.advanced || {};
  const setAdv = (k) => (e) => patch({ advanced: { ...adv, [k]: e.target.value } });
  const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—");

  return (
    <>
      <CollapsedCard icon={FileText} label="Quote Details" onAction={() => setOpen(true)} actionLabel="EDIT" testId="mobile-quote-details">
        <div className="grid grid-cols-2 gap-2.5 text-xs">
          <div><div className="text-zinc-400 uppercase text-[9px] font-bold tracking-widest">Issue</div><div className="mt-0.5 font-medium">{fmt(q.issue_date)}</div></div>
          <div><div className="text-zinc-400 uppercase text-[9px] font-bold tracking-widest">Valid until</div><div className="mt-0.5 font-medium">{fmt(q.valid_until)}{validDays != null && ` (${validDays}d)`}</div></div>
          {q.reference && <div className="col-span-2"><div className="text-zinc-400 uppercase text-[9px] font-bold tracking-widest">Reference</div><div className="mt-0.5 font-medium truncate">{q.reference}</div></div>}
        </div>
      </CollapsedCard>

      <MobileSheet open={open} onClose={() => setOpen(false)} title="Quote details" testId="mobile-details-sheet">
        <div className="space-y-3">
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Quote Number</Label>
            <Input value={q.quote_number || ""} readOnly className="h-11 rounded-lg mt-1.5 bg-zinc-50 dark:bg-white/[0.03] font-mono font-bold" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Issue Date</Label>
            <Input type="date" value={q.issue_date || ""} onChange={(e) => patch({ issue_date: e.target.value })} className="h-11 rounded-lg mt-1.5" data-testid="mobile-issue-date" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Valid Until</Label>
            <Input type="date" value={q.valid_until || ""} onChange={(e) => patch({ valid_until: e.target.value })} className="h-11 rounded-lg mt-1.5" data-testid="mobile-valid-until" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Reference</Label>
            <Input value={q.reference || ""} onChange={(e) => patch({ reference: e.target.value })} placeholder="e.g. Harbour View" className="h-11 rounded-lg mt-1.5" data-testid="mobile-reference" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Quote Title</Label>
            <Input value={q.title || ""} onChange={(e) => patch({ title: e.target.value })} placeholder="e.g. Stage 1 Drainage Works" className="h-11 rounded-lg mt-1.5" data-testid="mobile-quote-title" /></div>

          <button onClick={() => setAdvOpen(!advOpen)} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 pt-2">
            <ChevronRight className={`w-3 h-3 transition-transform ${advOpen ? "rotate-90" : ""}`} /> Advanced Details
          </button>
          {advOpen && (
            <div className="space-y-3 pt-2 border-t border-zinc-100 dark:border-white/5">
              {[["project_manager", "Project Manager"], ["salesperson", "Salesperson"], ["cost_centre", "Cost Centre"], ["po_number", "Purchase Order Number"], ["internal_ref", "Internal Reference"]].map(([k, label]) => (
                <div key={k}><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</Label>
                  <Input value={adv[k] || ""} onChange={setAdv(k)} className="h-11 rounded-lg mt-1.5" /></div>
              ))}
            </div>
          )}
        </div>
      </MobileSheet>
    </>
  );
}

// ==================== 3. LINE ITEMS ====================
export function MobileLineItems({ items, updateItem, removeItem, duplicateItem, moveItem, addItem, setItems, editable }) {
  const [editingIndex, setEditingIndex] = useState(null); // null closed | "new" | number
  const [libOpen, setLibOpen] = useState(false);
  const visibleItems = items.map((it, i) => ({ it, i }));

  const openNew = () => setEditingIndex("new");
  const closeEditor = () => setEditingIndex(null);

  const handleSaveNewFromLibrary = (draft) => {
    setItems([...items, draft]);
    setLibOpen(false);
  };

  return (
    <section className="border-2 border-zinc-300 dark:border-white/15 bg-white dark:bg-[#0F141C]" data-testid="mobile-quote-line-items">
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-white/[0.03]">
        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-600 dark:text-zinc-300">
          <Layers className="w-3.5 h-3.5 text-[#FF5F15]" /> Line Items <span className="text-zinc-400">{items.filter((i) => !i.is_section).length}</span>
        </div>
      </div>

      {visibleItems.length === 0 && (
        <div className="p-6 text-center text-xs text-zinc-400" data-testid="mobile-items-empty">No line items yet.</div>
      )}

      <div className="divide-y divide-zinc-100 dark:divide-white/5">
        {visibleItems.map(({ it, i }) => it.is_section ? (
          <div key={i} className="px-3.5 py-2 bg-zinc-100 dark:bg-white/[0.05] text-[10px] font-bold uppercase tracking-widest text-zinc-500" data-testid={`mobile-section-${i}`}>
            {it.description}
          </div>
        ) : (
          <MobileLineItemCard key={i} it={it} i={i} editable={editable}
            onEdit={() => setEditingIndex(i)}
            onDuplicate={() => duplicateItem(i)}
            onMoveUp={() => moveItem(i, -1)}
            onMoveDown={() => moveItem(i, 1)}
            onDelete={() => removeItem(i)}
          />
        ))}
      </div>

      {editable && (
        <div className="p-3 space-y-2">
          <Button onClick={openNew} className="w-full h-11 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-add-item-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Item
          </Button>
          <Button variant="outline" onClick={() => setLibOpen(true)} className="w-full h-11 rounded-lg text-[11px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="mobile-add-from-library-btn">
            <Library className="w-4 h-4 mr-1.5" /> Add from Rate Library
          </Button>
        </div>
      )}

      <MobileItemEditorSheet
        open={editingIndex !== null}
        item={editingIndex === "new" ? null : items[editingIndex]}
        onClose={closeEditor}
        onSave={(draft) => {
          if (editingIndex === "new") addItem(draft); else updateItem(editingIndex, draft);
          closeEditor();
        }}
      />
      <MobileRateLibrarySheet open={libOpen} onClose={() => setLibOpen(false)} onAdd={handleSaveNewFromLibrary} />
    </section>
  );
}

function MobileLineItemCard({ it, i, editable, onEdit, onDuplicate, onMoveUp, onMoveDown, onDelete }) {
  const total = lineTotal(it);
  const cost = lineCost(it);
  const margin = cost != null && total > 0 ? ((total - cost) / total) * 100 : null;
  return (
    <div className="px-3.5 py-3" data-testid={`mobile-item-card-${i}`}>
      <div className="flex items-start justify-between gap-2">
        <button onClick={onEdit} className="text-left min-w-0 flex-1" data-testid={`mobile-item-edit-${i}`}>
          <div className="font-bold text-sm truncate">{it.description || "Untitled item"}</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {it.qty || 0} {it.unit || "ea"} × {nzd(it.unit_price)}
            {!!it.discount_pct && <span className="text-emerald-600 dark:text-emerald-400"> · -{it.discount_pct}%</span>}
          </div>
        </button>
        {editable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 -mr-1.5 text-zinc-400 hover:text-zinc-800 dark:hover:text-white" data-testid={`mobile-item-menu-${i}`}>
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-lg">
              <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}>Duplicate</DropdownMenuItem>
              <DropdownMenuItem onClick={onMoveUp}>Move Up</DropdownMenuItem>
              <DropdownMenuItem onClick={onMoveDown}>Move Down</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`mobile-item-delete-${i}`}>Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex items-end justify-between mt-2">
        {cost != null ? (
          <div className="text-[10px] text-zinc-400 font-mono">Cost {nzd(cost)} · Margin {margin != null ? `${margin.toFixed(1)}%` : "—"}</div>
        ) : <span />}
        <div className="font-mono font-black text-base" data-testid={`mobile-item-total-${i}`}>{nzd(total)}</div>
      </div>
    </div>
  );
}

// ==================== ADD/EDIT ITEM SHEET ====================
const blankItem = () => ({
  description: "", detail: "", qty: 1, unit: "ea", unit_price: 0, discount_pct: 0,
  is_section: false, cost_code: "", category: "", labour_cost: null, material_cost: null,
  plant_cost: null, internal_note: "",
});
function MobileItemEditorSheet({ open, item, onClose, onSave }) {
  const [draft, setDraft] = useState(blankItem());
  const [showDetail, setShowDetail] = useState(false);
  const [showCosting, setShowCosting] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDraft(item ? { ...item } : blankItem());
    setShowDetail(!!item?.detail);
    setShowCosting(item && (item.labour_cost != null || item.material_cost != null || item.plant_cost != null || item.cost_code || item.internal_note));
  }, [open, item]);

  const set = (changes) => setDraft((d) => ({ ...d, ...changes }));
  const total = lineTotal(draft);
  const cost = lineCost(draft);
  const margin = cost != null && total > 0 ? ((total - cost) / total) * 100 : null;
  const canSave = (draft.description || "").trim().length > 0;

  return (
    <MobileSheet open={open} onClose={onClose} title={item ? "Edit item" : "Add item"} testId="mobile-item-editor-sheet"
      footer={
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2 text-center text-[10px]">
            <div><div className="text-zinc-400 uppercase tracking-widest">Qty</div><div className="font-bold mt-0.5">{draft.qty || 0} {draft.unit}</div></div>
            <div><div className="text-zinc-400 uppercase tracking-widest">Sell</div><div className="font-bold mt-0.5 font-mono">{nzd(total)}</div></div>
            <div><div className="text-zinc-400 uppercase tracking-widest">Cost</div><div className="font-bold mt-0.5 font-mono">{cost != null ? nzd(cost) : "—"}</div></div>
            <div><div className="text-zinc-400 uppercase tracking-widest">Margin</div><div className="font-bold mt-0.5">{margin != null ? `${margin.toFixed(1)}%` : "—"}</div></div>
          </div>
          <Button disabled={!canSave} onClick={() => onSave(draft)} className="w-full h-12 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-item-save-btn">
            {item ? "Update Item" : "Add to Quote"}
          </Button>
        </div>
      }>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Description</Label>
          <Input autoFocus value={draft.description} onChange={(e) => set({ description: e.target.value })} placeholder="e.g. Drainage Installation" className="h-11 rounded-lg mt-1.5" data-testid="mobile-item-desc-input" />
        </div>
        {!showDetail ? (
          <button onClick={() => setShowDetail(true)} className="text-[11px] font-bold text-[#FF5F15]" data-testid="mobile-item-add-detail">+ Add detailed description</button>
        ) : (
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Detailed description <span className="normal-case text-zinc-400">(shown to customer)</span></Label>
            <Textarea rows={2} value={draft.detail || ""} onChange={(e) => set({ detail: e.target.value })} className="rounded-lg mt-1.5" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Quantity</Label>
            <Input type="number" inputMode="decimal" step="0.01" value={draft.qty ?? ""} onChange={(e) => set({ qty: e.target.value })} className="h-11 rounded-lg mt-1.5" data-testid="mobile-item-qty-input" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Unit</Label>
            <Input value={draft.unit || ""} onChange={(e) => set({ unit: e.target.value })} placeholder="ea" className="h-11 rounded-lg mt-1.5" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Sell Rate</Label>
            <Input type="number" inputMode="decimal" step="0.01" value={draft.unit_price ?? ""} onChange={(e) => set({ unit_price: e.target.value })} className="h-11 rounded-lg mt-1.5" data-testid="mobile-item-rate-input" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Discount %</Label>
            <Input type="number" inputMode="decimal" step="1" min="0" max="100" value={draft.discount_pct ?? ""} onChange={(e) => set({ discount_pct: e.target.value })} className="h-11 rounded-lg mt-1.5" /></div>
        </div>

        <button onClick={() => setShowCosting(!showCosting)} className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 pt-1" data-testid="mobile-item-costing-toggle">
          <ChevronRight className={`w-3 h-3 transition-transform ${showCosting ? "rotate-90" : ""}`} /> Internal Costing
        </button>
        {showCosting && (
          <div className="space-y-3 pt-1 border-t border-zinc-100 dark:border-white/5">
            <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Cost Code</Label>
              <Input value={draft.cost_code || ""} onChange={(e) => set({ cost_code: e.target.value })} className="h-11 rounded-lg mt-1.5" /></div>
            <div className="grid grid-cols-3 gap-2">
              {[["labour_cost", "Labour"], ["material_cost", "Materials"], ["plant_cost", "Plant"]].map(([k, label]) => (
                <div key={k}><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</Label>
                  <Input type="number" inputMode="decimal" step="0.01" value={draft[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} className="h-11 rounded-lg mt-1.5 text-right" data-testid={`mobile-item-${k}-input`} /></div>
              ))}
            </div>
            <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Internal Notes <span className="normal-case text-red-500/80">— never shown to customer</span></Label>
              <Textarea rows={2} value={draft.internal_note || ""} onChange={(e) => set({ internal_note: e.target.value })} className="rounded-lg mt-1.5" /></div>
          </div>
        )}
      </div>
    </MobileSheet>
  );
}

// ==================== RATE LIBRARY ====================
const LIB_CATS = [
  { key: "all", label: "Frequent" }, { key: "material", label: "Materials" },
  { key: "labour", label: "Labour" }, { key: "plant", label: "Plant" }, { key: "service", label: "Services" },
];
function MobileRateLibrarySheet({ open, onClose, onAdd }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    if (!open) return;
    setSearch(""); setCat("all"); setPicked(null);
    api.get("/rate-library").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [open]);
  const matchCat = (it) => {
    if (cat === "all") return true;
    const c = (it.category || "").toLowerCase();
    if (cat === "material") return ["material", "materials", "concrete", "aggregate", "pipe", "drainage"].some((x) => c.includes(x));
    if (cat === "labour") return c.includes("labour") || c.includes("labor");
    if (cat === "plant") return ["plant", "equipment", "machine", "earthworks"].some((x) => c.includes(x));
    if (cat === "service") return c.includes("service");
    return true;
  };
  const filtered = items.filter((it) => (it.description || "").toLowerCase().includes(search.toLowerCase()) && matchCat(it));
  const recentIds = useMemo(() => { try { return JSON.parse(localStorage.getItem(RECENT_RATES_KEY) || "[]"); } catch { return []; } }, [open]);
  const recent = !search ? recentIds.map((id) => items.find((it) => it.id === id)).filter(Boolean) : [];

  const rememberRecent = (id) => {
    try {
      const cur = JSON.parse(localStorage.getItem(RECENT_RATES_KEY) || "[]");
      const next = [id, ...cur.filter((x) => x !== id)].slice(0, 8);
      localStorage.setItem(RECENT_RATES_KEY, JSON.stringify(next));
    } catch { /* noop */ }
  };

  const confirmAdd = (draft) => {
    rememberRecent(picked.id);
    onAdd(draft);
    setPicked(null);
  };

  return (
    <>
      <MobileSheet open={open && !picked} onClose={onClose} title="Rate library" testId="mobile-rate-library-sheet">
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input autoFocus placeholder="Search rates…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-11 rounded-lg pl-9" data-testid="mobile-library-search" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 mb-2 -mx-1 px-1">
          {LIB_CATS.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cat === c.key ? "bg-[#FF5F15] text-white border-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-500"}`}
              data-testid={`mobile-library-cat-${c.key}`}>{c.label}</button>
          ))}
        </div>
        {recent.length > 0 && (
          <>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mt-3 mb-1">Recent</div>
            <div className="divide-y divide-zinc-100 dark:divide-white/5 border border-zinc-200 dark:border-white/10 rounded-lg mb-3">
              {recent.map((it) => (
                <button key={it.id} onClick={() => setPicked(it)} className="w-full flex items-center justify-between px-3 py-2.5 text-left" data-testid={`mobile-library-recent-${it.id}`}>
                  <span className="text-sm font-medium truncate">{it.description}</span>
                  <span className="font-mono text-xs font-bold shrink-0 ml-2">{nzd(it.unit_price)}/{it.unit || "ea"}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mb-1">{recent.length > 0 ? "All rates" : ""}</div>
        <div className="divide-y divide-zinc-100 dark:divide-white/5 border border-zinc-200 dark:border-white/10 rounded-lg">
          {filtered.length === 0 && <div className="p-6 text-center text-xs text-zinc-400">Nothing matches. Build your rate library under Rates.</div>}
          {filtered.map((it) => (
            <button key={it.id} onClick={() => setPicked(it)} className="w-full flex items-center justify-between px-3 py-3 text-left" data-testid={`mobile-library-item-${it.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{it.description}</div>
                <div className="text-[10px] text-zinc-400 uppercase">{it.category || "uncategorised"}</div>
              </div>
              <span className="font-mono text-xs font-bold shrink-0 ml-2">{nzd(it.unit_price)}/{it.unit || "ea"}</span>
            </button>
          ))}
        </div>
      </MobileSheet>
      <MobileQuickQuantitySheet open={!!picked} item={picked} onClose={() => setPicked(null)} onAdd={confirmAdd} />
    </>
  );
}

function MobileQuickQuantitySheet({ open, item, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  useEffect(() => { if (open) setQty(item?.default_qty || 1); }, [open, item]);
  if (!item) return null;
  const total = lineTotal({ qty, unit_price: item.unit_price });
  const step = () => (item.unit === "hr" ? 0.5 : 1);
  return (
    <MobileSheet open={open} onClose={onClose} title={item.description} testId="mobile-quick-qty-sheet"
      footer={
        <Button disabled={!qty || qty <= 0} onClick={() => onAdd({
          description: item.description, qty, unit: item.unit || "ea", unit_price: item.unit_price || 0,
          discount_pct: 0, is_section: false, category: item.category || "", detail: "", internal_note: "",
        })} className="w-full h-12 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-quick-qty-add">
          Add to Quote
        </Button>
      }>
      <div className="text-center py-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Rate</div>
        <div className="font-mono font-black text-lg mt-1">{nzd(item.unit_price)} / {item.unit || "ea"}</div>
      </div>
      <div className="text-center py-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-3">How much?</div>
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => setQty((q) => Math.max(0, +(safeNumber(q) - step()).toFixed(2)))} className="w-12 h-12 rounded-full border-2 border-zinc-300 dark:border-white/15 flex items-center justify-center text-zinc-600 dark:text-zinc-300" data-testid="mobile-qty-minus">
            <Minus className="w-5 h-5" />
          </button>
          <div className="flex items-baseline gap-1.5">
            <Input type="number" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)}
              className="w-24 h-14 text-center text-2xl font-black rounded-lg" data-testid="mobile-qty-input" />
            <span className="text-sm text-zinc-500">{item.unit || "ea"}</span>
          </div>
          <button onClick={() => setQty((q) => +(safeNumber(q) + step()).toFixed(2))} className="w-12 h-12 rounded-full border-2 border-zinc-300 dark:border-white/15 flex items-center justify-center text-zinc-600 dark:text-zinc-300" data-testid="mobile-qty-plus">
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="text-center py-3 border-t border-zinc-100 dark:border-white/5">
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Total</div>
        <div className="font-mono font-black text-2xl text-[#FF5F15] mt-1">{nzd(total)}</div>
      </div>
    </MobileSheet>
  );
}

// ==================== 4. PRICING (discount + GST) ====================
export function MobilePricingCard({ q, patch }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <CollapsedCard icon={Percent} label="Pricing" onAction={() => setOpen(true)} actionLabel="EDIT" testId="mobile-pricing-card">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">{q.discount_type ? `${q.discount_type === "percentage" ? `${q.discount_value || 0}% off` : nzd(q.discount_value)} discount` : "No discount"}</span>
          <span className="text-zinc-500">{q.gst_inclusive ? "GST inclusive" : "GST added on top"}</span>
        </div>
      </CollapsedCard>
      <MobileSheet open={open} onClose={() => setOpen(false)} title="Pricing" testId="mobile-pricing-sheet">
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Discount Type</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {[["none", "None"], ["percentage", "%"], ["fixed", "$"]].map(([v, label]) => (
                <button key={v} onClick={() => patch({ discount_type: v === "none" ? null : v, ...(v === "none" ? { discount_value: null } : {}) })}
                  className={`h-11 rounded-lg text-xs font-bold uppercase border ${((q.discount_type || "none") === v) ? "bg-[#FF5F15] text-white border-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-500"}`}
                  data-testid={`mobile-discount-type-${v}`}>{label}</button>
              ))}
            </div>
          </div>
          {q.discount_type && (
            <>
              <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Value {q.discount_type === "percentage" ? "(%)" : "($)"}</Label>
                <Input type="number" inputMode="decimal" step="0.01" min="0" value={q.discount_value ?? ""} onChange={(e) => patch({ discount_value: e.target.value })} className="h-11 rounded-lg mt-1.5" data-testid="mobile-discount-value" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Reason (optional)</Label>
                <Input value={q.discount_reason || ""} onChange={(e) => patch({ discount_reason: e.target.value })} placeholder="e.g. Volume Discount" className="h-11 rounded-lg mt-1.5" /></div>
            </>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-100 dark:border-white/5">
            <div>
              <div className="text-sm font-bold">Prices Include GST</div>
              <div className="text-[10px] text-zinc-400 mt-0.5">{q.gst_inclusive ? "Line prices already include GST. No additional GST will be added to the quote total." : "GST will be added to the line prices shown above."}</div>
            </div>
            <Switch checked={!!q.gst_inclusive} onCheckedChange={(v) => patch({ gst_inclusive: !!v })} data-testid="mobile-gst-inclusive" />
          </div>
        </div>
      </MobileSheet>
    </>
  );
}

// ==================== 5. MORE (secondary sections) ====================
export function MobileMoreSection(props) {
  const { q, patch, setQ, jobs, uploadFiles, onEstimate, onDuplicate, onTemplate, onImportQuote, noteUrl } = props;
  const [open, setOpen] = useState(false);
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]" data-testid="mobile-more-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3.5 py-3" data-testid="mobile-more-toggle">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">More Options</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 border-t border-zinc-100 dark:border-white/5 pt-3">
          <LinkedJobSection q={q} patch={patch} jobs={jobs} />
          <AttachmentsSection q={q} setQ={setQ} uploadFiles={uploadFiles} />
          <NotesSection q={q} patch={patch} />
          <TermsSection q={q} patch={patch} />
          <SettingsPanel q={q} patch={patch} />
          <QuickActionsPanel onEstimate={onEstimate} onDuplicate={onDuplicate} onTemplate={onTemplate} onImportQuote={onImportQuote} />
          <ActivitySection q={q} setQ={setQ} noteUrl={noteUrl} />
        </div>
      )}
    </section>
  );
}

// ==================== 6. STICKY BOTTOM ACTION BAR ====================
export function MobileActionBar({ q, totals, saveState, onSaveDraft, onReview }) {
  const [expanded, setExpanded] = useState(false);
  const [ref, h] = useMeasuredHeight();
  useKiriClearance(h);
  return (
    <div ref={ref} className="md:hidden fixed bottom-0 inset-x-0 z-[60] border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0B0F14] px-3 pt-2 pb-3" data-testid="mobile-action-bar">
      <button onClick={() => setExpanded((s) => !s)} className="w-full flex items-center justify-between py-1" data-testid="mobile-total-toggle">
        <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          {saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved changes" : "Saved"}
        </span>
        <span className="font-mono font-black text-[#FF5F15] flex items-center gap-1">
          {nzd(totals.total)} <span className="text-[9px] font-sans text-zinc-400">NZD · INCLUDES GST</span> <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </span>
      </button>
      {expanded && (
        <div className="py-2 text-xs space-y-1 text-zinc-500 border-t border-zinc-100 dark:border-white/5 mt-1">
          <div className="flex justify-between"><span>Subtotal ({q.gst_inclusive ? "incl." : "excl."} GST)</span><span className="font-mono">{nzd(totals.subtotal)}</span></div>
          {totals.discount > 0 && <div className="flex justify-between"><span>Discount</span><span className="font-mono">-{nzd(totals.discount)}</span></div>}
          {!q.gst_inclusive && totals.discount > 0 && <div className="flex justify-between"><span>After discount (excl. GST)</span><span className="font-mono">{nzd(totals.net)}</span></div>}
            <div className="flex justify-between"><span>{q.gst_inclusive ? "GST included (15%)" : "GST (15%)"}</span><span className="font-mono">{nzd(totals.gst)}</span></div>
            {q.gst_inclusive && <div className="flex justify-between"><span>{totals.discount > 0 ? "After discount" : "Subtotal"} (excl. GST)</span><span className="font-mono">{nzd(totals.net)}</span></div>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mt-2">
        <Button variant="outline" onClick={onSaveDraft} className="h-11 rounded-lg text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-save-draft-btn">Save Draft</Button>
        <Button onClick={onReview} className="h-11 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-review-btn">
          Review <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ==================== 7. REVIEW & SEND ====================
export function MobileReviewScreen({ open, onClose, q, totals, items, onPreviewPdf, onSend }) {
  const [ref, h] = useMeasuredHeight();
  useKiriClearance(open ? h : 0, open);
  if (!open) return null;
  const lineCount = items.filter((i) => !i.is_section).length;
  const hasCustomer = !!(q.client_name || "").trim();

  return (
    <div className="fixed inset-0 z-[75] bg-white dark:bg-[#0B0F14] flex flex-col md:hidden" data-testid="mobile-review-screen">
      <div className="flex items-center gap-3 px-4 h-14 border-b border-zinc-200 dark:border-white/10 shrink-0">
        <button onClick={onClose} aria-label="Back to edit" className="text-zinc-500 hover:text-[#FF5F15] p-1 -ml-1"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">Review Quote</div>
          <div className="font-display font-black uppercase tracking-tight text-sm truncate">{q.quote_number}</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div className="space-y-2" data-testid="mobile-review-checklist">
          <ChecklistRow label="Customer" ok={hasCustomer} detail={q.client_name} />
          <ChecklistRow label="Quote Details" ok={!!q.issue_date} detail={q.reference} />
          <ChecklistRow label={`${lineCount} Line Item${lineCount === 1 ? "" : "s"}`} ok={lineCount > 0} />
        </div>

        <section className="border-2 border-[#FF5F15]/40 bg-white dark:bg-[#0F141C]" data-testid="mobile-review-summary">
          <div className="px-4 py-2.5 border-b border-zinc-100 dark:border-white/5 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-2">
            <CircleDollarSign className="w-3.5 h-3.5 text-[#FF5F15]" /> Quote Summary
          </div>
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Subtotal ({q.gst_inclusive ? "incl." : "excl."} GST)</span><span className="font-mono">{nzd(totals.subtotal)}</span></div>
            {totals.discount > 0 && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Discount</span><span className="font-mono">-{nzd(totals.discount)}</span></div>}
            {!q.gst_inclusive && totals.discount > 0 && <div className="flex justify-between"><span className="text-zinc-500">After discount (excl. GST)</span><span className="font-mono">{nzd(totals.net)}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">{q.gst_inclusive ? "GST included (15%)" : "GST (15%)"}</span><span className="font-mono">{nzd(totals.gst)}</span></div>
            {q.gst_inclusive && <div className="flex justify-between"><span className="text-zinc-500">{totals.discount > 0 ? "After discount" : "Subtotal"} (excl. GST)</span><span className="font-mono">{nzd(totals.net)}</span></div>}
            <div className="border-t-2 border-zinc-200 dark:border-white/10 pt-3 mt-3 flex items-end justify-between">
              <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Total</span>
              <div className="text-right"><span className="text-2xl font-black font-mono text-[#FF5F15]" data-testid="mobile-review-total">{nzd(totals.total)}</span><div className="text-[9px] font-bold text-zinc-400 mt-1">NZD · INCLUDES GST</div></div>
            </div>
          </div>
        </section>
      </div>

      <div ref={ref} className="shrink-0 border-t border-zinc-200 dark:border-white/10 p-3 space-y-2">
        <Button variant="outline" onClick={onPreviewPdf} className="w-full h-11 rounded-lg text-[11px] font-bold uppercase tracking-wider" data-testid="mobile-preview-customer-quote">
          <Eye className="w-4 h-4 mr-1.5" /> Preview Customer Quote
        </Button>
        <Button onClick={onSend} disabled={!hasCustomer || lineCount === 0} className="w-full h-12 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white text-[12px] font-bold uppercase tracking-wider" data-testid="mobile-send-quote-btn">
          <Send className="w-4 h-4 mr-1.5" /> Send Quote
        </Button>
      </div>
    </div>
  );
}

function ChecklistRow({ label, ok, detail }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-2.5 border border-zinc-200 dark:border-white/10 rounded-lg">
      <div>
        <div className="text-sm font-bold">{label}</div>
        {detail && <div className="text-xs text-zinc-500 mt-0.5 truncate">{detail}</div>}
      </div>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center ${ok ? "bg-emerald-500 text-white" : "bg-zinc-200 dark:bg-white/10 text-zinc-400"}`}>
        {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
      </span>
    </div>
  );
}
